import { ApiError } from "@config/apiClient";
import { useMutation } from "@tanstack/react-query";

import { queryClient } from "@config/queryClient";
import companyService from "@services/company.service";
import ToastService from "@services/toast.service";
import { ERROR_STRINGS, ERROR_MESSAGES } from "@config/messages";
import { useCreateMeetingObject } from "@screens/meeting/hooks/useCreateMeetingObject";
import { useBugsnagErrorHandler } from "@hooks/useBugsnagErrorHandler";
import { DisconnectedError } from "@app-types/errors";
import { ICreateCompanyFromExternal } from "@interfaces/company.interface";
import { RelationObjectType } from "@enums/relations.enum";
import {
  COMPANY_SUGGESTIONS_QUERY_KEY,
  CREATE_COMPANY_FROM_EXTERNAL_MUTATION_KEY,
} from "@config/query";

export function useCreateCompanyFromExternal(leftContentType: RelationObjectType) {
  const { notifyBugsnag } = useBugsnagErrorHandler();
  const { mutateAsync: createMeetingObject } = useCreateMeetingObject();

  return useMutation({
    mutationKey: [CREATE_COMPANY_FROM_EXTERNAL_MUTATION_KEY],
    mutationFn: async (data: ICreateCompanyFromExternal) => {
      return await companyService.createCompanyFromExternal(data);
    },
    onSuccess: (response) => {
      if (leftContentType === RelationObjectType.MEETING) {
        void createMeetingObject({
          object_type: RelationObjectType.COMPANY,
          object_id: response.company.id,
        });
      }
      // remove company suggestions query to prevent showing it in dropdown right after creation
      queryClient.removeQueries({ queryKey: [COMPANY_SUGGESTIONS_QUERY_KEY] });

      ToastService.showSuccess(`"${response.company.title}" company created`);
    },
    onError: (error) => {
      if (error instanceof DisconnectedError) return;
      notifyBugsnag(error, ERROR_STRINGS.CREATE_COMPANY);
      ToastService.showRejection(error as ApiError, ERROR_MESSAGES.CREATE_COMPANY);
    },
  });
}
